import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Routes } from "../../navigation";
import { transformRoute, useTranslate } from "helpers";
import { CalculationSearchPageCmp, CalculationSearchPageProps } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { DataWithPagination } from "services/api";

import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

import { useSelectedItemsChanges } from "ui/components/Table/hooks";

import { NoResults } from "ui/components/NoResults";

import {
  CalculationRow,
  CalculationSearchFormValues,
} from "services/api/calculations/types";
import { CalculationEditCard } from "../CalculationEditPage/components/CalculationEditCard";

import { CALCULATION_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { getCalculationsAPI } from "services/api/calculations/api";
import CalculationSearchFilters from "./components/CalculationSearchFilters/CalculationSearchFilters";
import CalculationSearchResults from "./components/CalculationSearchResults/CalculationSearchResults";

const CalculationSearchPage: CalculationSearchPageCmp = (
  props: CalculationSearchPageProps
) => {
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<CalculationRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [calculationId, setCalculationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const { t } = useTranslate();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getCalculationsAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(
    (id: string) => {
      navigate(
        transformRoute(Routes.CalculationDetailPageRouter, {
          calculationId: id,
        })
      );
    },
    [navigate]
  );

  const handleSearchChange = useCallback(
    (searchFormValues: CalculationSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setCalculationId(null);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleCreateClick = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCreateModalClose = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults.pagination]);

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={CALCULATION_SEARCH_PAGE_TITLE.text}
              icon={CALCULATION_SEARCH_PAGE_TITLE.icon}
              iconColor={CALCULATION_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <CalculationSearchFilters
            onSearchChange={handleSearchChange}
            onCreateClick={handleCreateClick}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t('calculation.search.noresults')} />
          ) : (
            <CalculationSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
        <CalculationEditCard
          show={showCreateModal}
          onClose={handleCreateModalClose}
          onSuccess={handleCreateSuccess}
        />
      </div>
    </>
  );
};

CalculationSearchPage.route = Routes.CalculationSearchPageRouter;

export default CalculationSearchPage;
